/** Qué es cada paso de una solicitud para quien lo pulsa: cómo se llama el botón, si pide una nota
 * y qué se le pregunta.
 *
 * El servidor decide qué pasos están permitidos (`estados_posibles`); esto sólo los nombra. Los
 * nombres hablan de **lo que se hace**, no del estado al que se llega: «Enviar a cierre» dice más
 * que «Pendiente de cierre».
 */
import type { EstadoSolicitud } from '../tipos'

export interface Accion {
  texto: string
  /** `obligatoria`: no se puede confirmar sin escribirla (el servidor también la exige). */
  nota: 'no' | 'opcional' | 'obligatoria'
  pregunta?: string
  estilo?: 'principal' | 'malo'
  ayuda?: string
}

type Clave = `${EstadoSolicitud}>${EstadoSolicitud}`

const ACCIONES: Partial<Record<Clave, Accion>> = {
  // Empezar
  'approved>in_development': { texto: 'Empezar (en curso)', nota: 'no', estilo: 'principal' },
  'planned>in_development': { texto: 'Empezar (en curso)', nota: 'no', estilo: 'principal' },
  'approved>planned': { texto: 'Planificarla', nota: 'no' },
  'in_development>planned': { texto: 'Aparcarla (planificada)', nota: 'no' },

  // Pruebas
  'in_development>testing': {
    texto: 'Pasar a pruebas', nota: 'opcional',
    pregunta: 'Qué se está probando y dónde (opcional)',
  },
  'testing>in_development': {
    texto: 'Volver a desarrollo', nota: 'opcional',
    pregunta: 'Qué ha fallado en las pruebas (opcional)',
  },

  // Cierre
  'in_development>closing': {
    texto: 'Enviar a cierre', nota: 'opcional', estilo: 'principal',
    pregunta: 'Qué debe comprobar administración (opcional)',
    ayuda: 'Administración la comprobará y la cerrará, o te la devolverá diciendo qué falta.',
  },
  'testing>closing': {
    texto: 'Enviar a cierre', nota: 'opcional', estilo: 'principal',
    pregunta: 'Qué debe comprobar administración (opcional)',
    ayuda: 'Administración la comprobará y la cerrará, o te la devolverá diciendo qué falta.',
  },
  'in_development>completed': {
    texto: 'Cerrar como completada', nota: 'opcional',
    pregunta: 'Mensaje para quien la pidió (opcional)',
    ayuda: 'La cierra sin pasar por la comprobación de administración.',
  },
  'testing>completed': {
    texto: 'Cerrar como completada', nota: 'opcional',
    pregunta: 'Mensaje para quien la pidió (opcional)',
    ayuda: 'La cierra sin pasar por la comprobación de administración.',
  },
  'closing>completed': {
    texto: 'Cerrar (comprobada)', nota: 'opcional', estilo: 'principal',
    pregunta: 'Mensaje para quien la pidió (opcional)',
  },
  'closing>in_development': {
    texto: 'Devolver a desarrollo', nota: 'obligatoria',
    pregunta: 'Qué falta o qué ha fallado',
    ayuda: 'Quien la lleva leerá esto: sin saber qué falta, la reenviaría igual.',
  },
}

export function accionDe(
  origen: EstadoSolicitud,
  destino: EstadoSolicitud,
  /** Quien pulsa decide sobre las solicitudes (administración), no sólo la lleva o la pidió. */
  decide: boolean,
): Accion {
  const conocida = ACCIONES[`${origen}>${destino}`]
  if (conocida) {
    // Quien lleva el desarrollo y retira su propio cierre no «devuelve» nada a nadie.
    if (origen === 'closing' && destino === 'in_development' && !decide) {
      return { ...conocida, texto: 'Retirar de cierre', pregunta: 'Por qué la retiras' }
    }
    return conocida
  }
  if (destino === 'reviewing') return { texto: 'Ya he añadido la información', nota: 'no' }
  if (destino === 'cancelled') {
    return decide
      ? { texto: 'Cancelar', nota: 'opcional', estilo: 'malo', pregunta: 'Por qué se cancela (opcional)' }
      : { texto: 'Retirar la solicitud', nota: 'no', estilo: 'malo' }
  }
  return { texto: ESTADO_POR_DEFECTO[destino] ?? destino, nota: 'no' }
}

const ESTADO_POR_DEFECTO: Partial<Record<EstadoSolicitud, string>> = {
  reviewing: 'En revisión',
  needs_info: 'Falta información',
  planned: 'Planificada',
  in_development: 'En desarrollo',
  testing: 'En pruebas',
  closing: 'Pendiente de cierre',
  completed: 'Completada',
}
